import { useState, useEffect, useMemo, useRef } from 'react';
import { Input } from '@/components/ui/input';
import LocationOptionLabel from '@/components/LocationOptionLabel';
import { NumberInput } from '@/components/ui/number-input';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CalendarIcon, MapPin } from 'lucide-react';
import { format } from 'date-fns';
import api from '@/lib/api';
import {
  splitQuantityComposite,
  formatQuantityComposite,
  filterUomsForType,
  defaultUomForType,
  rankUomsForSuggestion,
} from '@/lib/quantityComposite';
import { uomQuantityType } from '@/lib/lineItemValidation';
import { autoResizeTextarea, TEXTAREA_BASE_ROWS, GROWING_TEXTAREA_CLASS } from '@/lib/autoResizeTextarea';

// Backend normalizes field_key onto every business question (see
// _normalize_business_question_field_keys in agents.py) - trust it directly
// instead of re-detecting question type from free text here, which was
// fragile to LLM drift (e.g. a "when will your company launch its website"
// question getting answer_type "date" and rendering a date picker).
const ALLOWED_DATE_FIELD_KEYS = ['submission_deadline', 'need_by_date', 'start_date', 'end_date'];

// Date Picker Component. UI POLISH (2026-07-28 ruling): month/year
// navigation is now the SHARED Calendar component's own caption
// (fixed-height grid + month/year dropdowns alongside the arrows - see
// components/ui/calendar.jsx) - the custom hidden-caption header this
// component used to render (prev/next-year jump buttons only, no
// dropdown, and no fix for the popover's own height jumping between a
// 5- and 6-week month) is gone; one shared implementation now backs
// every date field in the app.
const DatePickerInput = ({ value, onChange, placeholder }) => {
  const [open, setOpen] = useState(false);

  const handleSelect = (date) => {
    if (date) {
      const formatted = format(date, 'yyyy-MM-dd');
      onChange(formatted);
    }
    setOpen(false);
  };

  const parsedDate = value ? new Date(value) : undefined;
  const isValidDate = parsedDate && !isNaN(parsedDate.getTime());

  // Item 7 (date-validation-fixes batch, 2026-07-23): disable past-date
  // selection as a first line of defense - validation (HVND/HVSD/HVPT etc,
  // via the shared dateValidationRules) stays the backstop regardless.
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="w-full justify-start text-left font-normal bg-zinc-50 border-zinc-200 hover:bg-zinc-100"
        >
          <CalendarIcon className="mr-2 h-4 w-4 text-zinc-500" />
          {isValidDate ? (
            <span>{format(parsedDate, 'MMMM dd, yyyy')}</span>
          ) : (
            <span className="text-zinc-400">{placeholder || 'Select date'}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" sideOffset={4}>
        <Calendar
          mode="single"
          selected={isValidDate ? parsedDate : undefined}
          onSelect={handleSelect}
          defaultMonth={isValidDate ? parsedDate : undefined}
          initialFocus
          disabled={{ before: startOfToday }}
        />
      </PopoverContent>
    </Popover>
  );
};

// Location Dropdown Component
//
// PRE-FILL (Phase 2 slice 3, Ashok ruling 2026-08-06). `proposal` is
// {code, confidence} from the intake location matcher (see
// ParsedRequirementFields.getLocationProposal). When confidence is
// "signal_present" the dropdown starts PRE-SELECTED with that code; when
// it is "none", or the proposal is absent, nothing is pre-selected and
// the buyer chooses as before.
//
// The pre-fill lives HERE, not in StepAnswers, for one reason: the
// membership check must run against the options this component actually
// renders. StepAnswers fetches its own copy of the list for submit-time
// validation, and checking against that copy would be checking against a
// different array than the one the Select is built from - the assumption
// that produces exactly the defect the check exists to prevent.
//
// FOUR CONSTRAINTS, all load-bearing:
//  1. Never auto-submits. This sets the control's value; the question
//     stays mandatory and the buyer still clicks Continue.
//  2. Only pre-fills a code present in the CURRENTLY LOADED options. A
//     code deactivated in master since intake would otherwise set a
//     Select value with no matching SelectItem and render BLANK - the
//     same defect class as the line-item grid. Skipped and logged.
//  3. Provenance is visible (the line under the control). A silently
//     pre-filled field can be accepted without review; naming what was
//     suggested is the point-of-use disclosure.
//  4. If the list fails to load, no pre-fill at all - the membership
//     check is untestable, so the fail-safe is to do nothing (same rule
//     as b953bd5's resolvability gate).
const LocationDropdown = ({ value, onChange, proposal, onPrefill }) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  // One attempt per mount, whatever the outcome. Without this, clearing
  // a pre-filled selection would immediately re-apply it and the buyer
  // could never choose "not this one".
  const prefillAttempted = useRef(false);

  useEffect(() => {
    let isMounted = true;
    const fetchLocations = async () => {
      try {
        const response = await api.getLocations();
        if (isMounted) {
          setLocations(response.data.locations || []);
        }
      } catch (err) {
        console.debug('Locations fetch failed (using text input fallback):', err.message);
        // Locations failed to load - fields will use text input fallback
        if (isMounted) {
          setLocations([]);
          setLoadFailed(true);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    fetchLocations();
    return () => { isMounted = false; };
  }, []);

  // ONE rule, ONE place. "Is this proposal usable at all?" was briefly
  // written twice - here and again inside the effect - and mutation
  // testing showed the duplication had made the rule untestable: deleting
  // either copy changed no behaviour, because the other still enforced
  // it. Two checks of the same condition are not defence in depth when
  // they are the same condition; they are one rule that no test can pin.
  const hasUsableProposal = Boolean(
    proposal && proposal.confidence === 'signal_present' && proposal.code
  );

  const proposedOption = hasUsableProposal
    ? locations.find(loc => loc.location_id === proposal.code)
    : undefined;

  useEffect(() => {
    if (loading || prefillAttempted.current) return;
    if (!hasUsableProposal) return;

    // Marked BEFORE any early return below: a proposal that could not be
    // applied must not be retried on a later render either.
    prefillAttempted.current = true;

    if (loadFailed) {
      console.warn(
        '[location pre-fill] skipped: the locations list failed to load, so the proposed code ' +
        `${proposal.code} could not be checked against the active options. Buyer chooses manually.`
      );
      return;
    }
    // Never overwrite an answer that already exists - a resumed wizard,
    // or a buyer who answered before this effect settled.
    if (value) return;
    if (!proposedOption) {
      console.warn(
        `[location pre-fill] skipped: proposed code ${proposal.code} is not among the ` +
        `${locations.length} active location options (deactivated in master since intake?). ` +
        'Pre-filling it would render a blank Select. Buyer chooses manually.'
      );
      return;
    }
    (onPrefill || onChange)(proposal.code);
  }, [loading, loadFailed, locations, proposal, hasUsableProposal, proposedOption, value, onChange, onPrefill]);

  const selectedLocation = locations.find(loc => loc.location_id === value);

  return (
    <div className="space-y-2">
      <Select value={value || ''} onValueChange={onChange}>
        <SelectTrigger className="w-full bg-zinc-50 border-zinc-200">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-zinc-500" />
            {/* UI POLISH (2026-07-28 ruling), item 2: an explicit
                children override keeps the closed/selected trigger
                compact (CODE - Name only) regardless of the richer
                two-line content each option below now carries -
                Radix's default behavior mirrors a selected item's full
                rendered content into the trigger, which would otherwise
                pull the full address in here too. */}
            <SelectValue placeholder={loading ? "Loading locations..." : "Select ship-to location"}>
              {selectedLocation ? `${selectedLocation.location_id} - ${selectedLocation.name}` : undefined}
            </SelectValue>
          </div>
        </SelectTrigger>
        <SelectContent>
          {locations.map(loc => (
            <SelectItem key={loc.location_id} value={loc.location_id}>
              {/* Extracted to LocationOptionLabel on 2026-08-23 - the same
                  markup, now shared with the Lines editor's ship-to cell so
                  the two pickers cannot drift. The address is still the
                  same field the post-selection card below displays: one
                  source, no second derivation (was city/state, a
                  different, less complete field). */}
              <LocationOptionLabel code={loc.location_id} name={loc.name} address={loc.address} />
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* PROVENANCE (constraint 3). Rendered whenever the proposal is
          resolvable in the current options - so it is present both when
          the value was pre-filled and after the buyer overrides it,
          because "what was suggested" stays true either way and the
          override is worth showing against something.
          Deliberately NOT rendered when the proposal could not be
          resolved: naming a code the dropdown cannot offer would be a
          claim the control contradicts. */}
      {proposedOption && (
        <p className="text-xs text-zinc-500" data-testid="location-proposal-provenance">
          Suggested from your requirement: {proposedOption.name} ({proposedOption.location_id})
        </p>
      )}

      {selectedLocation && (
        <div className="bg-blue-50 border border-blue-200 p-3 rounded-sm text-sm">
          <div className="font-medium text-blue-900">{selectedLocation.name}</div>
          <div className="text-blue-700 text-xs mt-1">{selectedLocation.address}</div>
        </div>
      )}
    </div>
  );
};

// Date placeholders by type
const DATE_PLACEHOLDERS = {
  start_date: 'Select start date',
  end_date: 'Select end date',
  need_by_date: 'Select need by date',
  submission_deadline: 'Select deadline'
};

// PROC-000054 item 1 (revised): quantity is a COMPOSITE value - a number
// plus a unit from uom_master - not a bare number (superseded the prior
// numeric-only fix, which invited answers like "10" with no unit at all).
// Self-fetches uom_master (same self-contained pattern as LocationDropdown
// above) so SmartAnswerInput's own value/onChange API stays a single
// string - the two sub-fields are composed into/split out of ONE canonical
// "{number} {UnitName}" string via quantityComposite.js, never re-parsed
// fuzzily here (see that module's doc comment for why).
const QuantityCompositeInput = ({ value, onChange, testId, procurementType, statedUnit, statedValue }) => {
  const [uoms, setUoms] = useState([]);
  const [loadingUoms, setLoadingUoms] = useState(true);
  // Value+unit default fix (2026-07-26): unlike displayUnit below (a
  // reactive fallback formula that's always safe to recompute, since a
  // Select can't be "cleared" back to a text-typed blank the way a number
  // input can), the stated VALUE must commit through a one-shot ref guard,
  // never a reactive fallback - a reactive `numberPart || statedValue`
  // formula would silently redisplay the stated number the instant a user
  // cleared their own entry (numberPart empty again -> fallback kicks back
  // in), making it impossible to leave the field genuinely blank.
  const statedDefaultAppliedRef = useRef(false);

  useEffect(() => {
    let isMounted = true;
    const fetchUoms = async () => {
      try {
        const response = await api.getMasterUoms('selection');
        if (isMounted) setUoms(response.data || []);
      } catch (err) {
        console.debug('UoM master fetch failed (unit selection will be empty):', err.message);
        if (isMounted) setUoms([]);
      } finally {
        if (isMounted) setLoadingUoms(false);
      }
    };
    fetchUoms();
    return () => { isMounted = false; };
  }, []);

  const filteredUoms = useMemo(() => filterUomsForType(uoms, procurementType), [uoms, procurementType]);
  const { numberPart, unitPart } = splitQuantityComposite(value);
  // Goods default (item a): pre-select "Each" in the dropdown when no unit
  // has been chosen/seeded yet.
  const defaultUnit = useMemo(() => defaultUomForType(procurementType, filteredUoms), [procurementType, filteredUoms]);
  // Item 1 (unit default from stated volume, defect fix): statedUnit, when
  // given, is the caller's own echo_metadata-derived, uom_master-matched
  // unit (see lib/statedIntentValidationRules.js's boundStatedVolume +
  // matchStatedUnitAgainstMaster) - i.e. what the buyer actually stated
  // ("600 sf of carpet" -> Sqft). PRECEDENCE FIX (2026-07-26): statedUnit
  // must outrank defaultUnit ("Each") - a generic goods fallback is only
  // for when NOTHING was stated; it must never mask a unit the requirement
  // itself named. The previous order (defaultUnit before statedUnit) let a
  // goods carpet line default to "Each" instead of the stated "Sqft", and
  // - after the 9fe67e3 commit-on-render fix - that wrong "Each" default
  // then got WRITTEN into the answer automatically.
  const displayUnit = unitPart || statedUnit || defaultUnit || '';

  // SPLIT-SOURCE-OF-TRUTH FIX (2026-07-26): displayUnit used to be a PURE
  // display fallback - shown in the trigger/Select but never written into
  // the answer until the user touched the unit dropdown themselves. That
  // left a real gap whenever a number ended up stored with no unit BEFORE
  // this component ever rendered it (a resumed procurement's stray bare
  // "1200", a StepAnswers-level effect that clears a wrong unit and leaves
  // the number bare, or any other future write path) - the composite
  // LOOKED complete (e.g. "1200 Sqft" shown via the stated-unit fallback)
  // while the STORED answer stayed the bare number, so StepAnswers.jsx's
  // submit validator (which reads the answer string directly, never this
  // component's display state) rejected it with "Please select a unit for
  // the quantity" - fixable only by a manual dropdown round-trip (pick a
  // different unit, then back), since THAT path calls onChange for real.
  // Fix: the moment a number exists with no unit chosen yet and a fallback
  // resolves, commit it through the SAME onChange path a manual selection
  // uses - one write path, so display/stored answer/validation always
  // agree. A still-empty number is untouched (formatQuantityComposite
  // returns '' regardless of unit when there's no number), so "please
  // provide the quantity" still fires correctly on a genuinely blank
  // question - only the "number present, unit still owed" half-state is
  // ever auto-resolved.
  useEffect(() => {
    if (!numberPart || unitPart || !displayUnit) return;
    onChange(formatQuantityComposite(numberPart, displayUnit));
  }, [numberPart, unitPart, displayUnit, onChange]);

  // VALUE default fix (2026-07-26, services trace): value and unit must
  // default from the SAME bound echo intent - statedValue (the intent's
  // quantities[0].value, e.g. 2 for "2 SAP consultants") had no path into
  // the answer at all for services (the StepAnswers-level prefill effect
  // that seeds goods/mixed is gated OFF for services), so a confirm-style
  // question ("You have indicated 2 SAP consultants...") rendered with its
  // unit defaulted but the number empty, contradicting its own wording.
  // Fires AT MOST ONCE per mounted instance (statedDefaultAppliedRef) -
  // not the reactive `numberPart || displayUnit` pattern the unit commit
  // above uses, since that would silently redisplay the stated number the
  // instant a user cleared their own entry, making a genuine blank
  // impossible. Multi-intent (statedValue null - see boundStatedVolume's
  // single-intent binding rule) never fires this, matching the unit
  // default's own existing silence there.
  useEffect(() => {
    if (statedDefaultAppliedRef.current) return;
    if (numberPart || statedValue == null || !displayUnit) return;
    statedDefaultAppliedRef.current = true;
    onChange(formatQuantityComposite(statedValue, displayUnit));
  }, [numberPart, statedValue, displayUnit, onChange]);

  // Item E (MIXED-SEMANTICS + ECHO FIX BATCH) - display-only ranking, same
  // doctrine as CategorySelectCell's category tiers: a "Suggested" tier
  // (the stated unit itself, plus anything sharing its dimension) rendered
  // above the complete, unfiltered master list - ordering, never
  // restricting. SARU (checkStatedUnitDimensionEcho) is the actual guard
  // against a cross-dimension pick; this is just a convenience ranking.
  // rankUomsForSuggestion's own `all` is DELIBERATELY the complete,
  // unfiltered list (see quantityComposite.test.js - "never filtering"), so
  // that contract stays untouched here.
  const uomTiers = useMemo(() => rankUomsForSuggestion(filteredUoms, statedUnit || null), [filteredUoms, statedUnit]);
  // "SqftSqft" trigger-duplication bug (2026-07-26): the stated unit
  // appeared in BOTH `suggested` and `all` (by design - `all` is never
  // filtered), so the SelectContent rendered TWO <SelectItem value="Sqft">
  // nodes. Radix's Select associates its trigger's displayed text with
  // EVERY mounted item sharing the selected value, not just one - two
  // items sharing a value means the trigger renders both items' text
  // concatenated ("SqftSqft"), not a de-duplicated single label. The
  // stored ANSWER was never affected (formatQuantityComposite/onChange
  // only ever see a single uom_name string from whichever item was
  // clicked) - this was display-only, confirmed by asserting the actual
  // answer string in StepAnswers.test.jsx's regression test. Fixed here,
  // at the only layer that actually puts two same-value nodes in the DOM:
  // the "All units" tier renders everything MINUS whatever the "Suggested"
  // tier already shows - every unit stays selectable from exactly one
  // visible tier, never zero, so "ranking, never restricting" still holds.
  const suggestedNames = useMemo(
    () => new Set(uomTiers.suggested.map((u) => u.uom_name)),
    [uomTiers.suggested]
  );
  const remainingAll = useMemo(
    () => uomTiers.all.filter((u) => !suggestedNames.has(u.uom_name)),
    [uomTiers.all, suggestedNames]
  );

  // THE WHOLE-NUMBER RULE ON THE COMPOSITE (Ashok ruling, 2026-08-24).
  // displayUnit is the unit in effect and the one formatQuantityComposite
  // will store, so it is what the rule must read - unitPart alone would
  // miss a stated/default unit the buyer can see in the Select but has not
  // re-picked.
  //
  // ONE HELPER, SHARED WITH THE LINES EDITOR AND MIRRORED ON THE SERVER:
  // lib/lineItemValidation's uomQuantityType. It is DELIBERATELY NOT
  // statedIntentValidationRules' findUomSemantics - that helper defaults a
  // MISSING row to 'procurement_object' as a fail-safe-to-FIRE for SARU,
  // and here the fail-safe runs the other way. Do not "align" them: an
  // unknown unit must not be converted into a decision. null falls through
  // and nothing is refused.
  const quantityTypeMap = useMemo(
    () => Object.fromEntries(
      (uoms || []).filter((u) => u.uom_name && u.quantity_type)
        .map((u) => [u.uom_name, u.quantity_type])
    ),
    [uoms]
  );
  const unitQuantityType = uomQuantityType(displayUnit, quantityTypeMap);
  const fractionalOnWholeUnit =
    unitQuantityType === 'integer'
    && numberPart !== ''
    && !Number.isNaN(Number(numberPart))
    && Number(numberPart) % 1 !== 0;

  const handleNumberChange = (newNumber) => {
    onChange(formatQuantityComposite(newNumber, displayUnit));
  };

  const handleUnitChange = (newUnit) => {
    // Quantity always represents procurement VOLUME - a unit change never
    // rewrites or auto-calculates the number, including a switch to a
    // calendar/time unit. Cross-dimension changes are flagged by SARU at
    // submit, not assisted here.
    onChange(formatQuantityComposite(numberPart, newUnit));
  };

  return (
    <div>
      <div className="flex gap-2">
        <NumberInput
          min="0"
          /* step FROM THE CHOSEN UNIT (2026-08-24), replacing the
             unconditional step="any". UNDEFINED when the unit is unknown -
             omitting the attribute is the permissive state, where step="1"
             would assert whole-only about a unit nobody classified. The
             estimated-budget NumberInput below KEEPS step="any"
             unconditionally: money is legitimately fractional. */
          step={
            unitQuantityType === 'integer' ? '1'
              : unitQuantityType === 'decimal' ? 'any'
                : undefined
          }
          value={numberPart}
          onChange={(e) => handleNumberChange(e.target.value)}
          placeholder="Quantity"
          className={`bg-zinc-50 border-zinc-200 focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF] flex-1 ${fractionalOnWholeUnit ? 'border-red-500 ring-1 ring-red-500' : ''}`}
          data-testid={testId}
        />
        <Select value={displayUnit} onValueChange={handleUnitChange}>
          <SelectTrigger className="w-40 bg-zinc-50 border-zinc-200" data-testid={`${testId}-uom`}>
            <SelectValue placeholder={loadingUoms ? 'Loading...' : 'Select unit'} />
          </SelectTrigger>
          <SelectContent>
            {uomTiers.suggested.length > 0 && (
              <SelectGroup>
                <SelectLabel className="text-[10px] uppercase tracking-wide text-zinc-400">Suggested</SelectLabel>
                {uomTiers.suggested.map((u) => (
                  <SelectItem key={`suggested-${u.uom_code || u.uom_name}`} value={u.uom_name}>{u.uom_name}</SelectItem>
                ))}
              </SelectGroup>
            )}
            {remainingAll.length > 0 && (
              <SelectGroup>
                {uomTiers.suggested.length > 0 && (
                  <SelectLabel className="text-[10px] uppercase tracking-wide text-zinc-400">All units</SelectLabel>
                )}
                {remainingAll.map((u) => (
                  <SelectItem key={`all-${u.uom_code || u.uom_name}`} value={u.uom_name}>{u.uom_name}</SelectItem>
                ))}
              </SelectGroup>
            )}
          </SelectContent>
        </Select>
      </div>
      {fractionalOnWholeUnit && (
        <p className="text-xs text-red-600 font-medium mt-1" data-testid={`${testId}-whole-number-error`}>
          {`Quantity must be a whole number for ${displayUnit}`}
        </p>
      )}
      <p className="text-xs text-zinc-500 mt-1">
        Confirm or update — the unit travels to your line items.
      </p>
    </div>
  );
};

// AI-generated answer suggestion chips (question.suggestions from the backend)
const AnswerSuggestionChips = ({ suggestions, onSelect }) => {
  if (!suggestions || suggestions.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {suggestions.map((s, i) => (
        <button
          key={`suggestion-${i}`}
          type="button"
          onClick={() => onSelect(s)}
          className="text-xs bg-amber-50 border border-amber-200 text-amber-700 px-2 py-1 rounded hover:bg-amber-100 hover:border-amber-300 transition-colors"
        >
          {s}
        </button>
      ))}
    </div>
  );
};

// FIX 4, ship-to-location trace (2026-07-30): field_key alone was
// selecting LocationDropdown, so a mislabelled question (backend
// normalizer false positive) rendered an unanswerable dropdown - a
// weight-capacity spec question or a yes/no installation question has
// no organization-location answer to pick. A genuine ship_to_location
// question is answer_type "location" (per question_generator_agent.txt's
// LOCATION FIELD TYPE RULE) or, for a question left completely untyped,
// unset - never "text"/"date"/"number". Defense in depth alongside the
// backend's own word-boundary + answer_type gates (agents.py) and
// cardinality gate (routes/procurement.py) - a mislabelled question
// degrades to the plain text input below, never to a control it can't
// answer.
const _LOCATION_COMPATIBLE_ANSWER_TYPES = new Set([undefined, null, '', 'location']);

// ONE DECISION TABLE, TWO CONSUMERS (Questions/Intake layout slice,
// 2026-09-03). Which control a business question renders is decided by
// its (answer_type, field_key) pair - never by question text - and the
// Answers step now also needs to know, BEFORE rendering, whether that
// control is a short one (a picker or a number, fit for half a row) or a
// free-text one (full width). Writing that second decision as its own
// if-chain next to this one would have created a same-file mirror of the
// branch order below, guarded by nothing but a comment. So the branch
// order lives HERE, once, as data: SmartAnswerInput switches on it, and
// answerLayoutWidth maps it to a width. Adding a control means adding a
// kind here and a case in the switch - the width follows by construction.
//
// Branch order is the pre-existing one, preserved exactly:
//   location  - ship_to_location with a location-compatible answer_type
//   date      - answer_type "date" on an allowed date field_key
//   budget    - field_key budget (currency chip + number)
//   quantity  - field_key quantity (the number + unit composite)
//   currency  - field_key rfp_currency; rendered by StepAnswers itself
//               (a Select), never reaches this component - listed so the
//               width resolver covers every row the Answers step draws
//   textarea  - anything else that carries suggestion chips
//   number    - answer_type "number" with no dedicated control above;
//               renders the plain input, but IS a numeric answer
//   text      - answer_type "text", or none (normalizeQuestion's default)
//   unrecognised - any other answer_type; renders the plain input
export const resolveAnswerControl = ({ answerType, fieldKey, suggestions = [] }) => {
  if (fieldKey === 'ship_to_location' && _LOCATION_COMPATIBLE_ANSWER_TYPES.has(answerType)) return 'location';
  if (answerType === 'date' && ALLOWED_DATE_FIELD_KEYS.includes(fieldKey)) return 'date';
  if (fieldKey === 'budget') return 'budget';
  if (fieldKey === 'quantity') return 'quantity';
  if (fieldKey === 'rfp_currency') return 'currency';
  if ((suggestions || []).length > 0) return 'textarea';
  if (answerType === 'number') return 'number';
  if (answerType === 'text' || answerType === undefined || answerType === null || answerType === '') return 'text';
  return 'unrecognised';
};

// Ruling 2 (Ashok, 2026-09-03): pair by ANSWER TYPE, two columns, and
// anything unrecognised defaults to FULL width - a wrongly-narrowed field
// is worse than a wastefully wide one. So this is an allow-list of the
// controls that are SHORT; every kind not named here, including
// 'unrecognised' and a free-text 'text'/'textarea', is full. A new
// control kind added to resolveAnswerControl is therefore full until
// someone decides otherwise here.
export const SHORT_ANSWER_CONTROLS = new Set(['location', 'date', 'budget', 'quantity', 'currency', 'number']);

export const answerLayoutWidth = (question) =>
  (SHORT_ANSWER_CONTROLS.has(resolveAnswerControl(question)) ? 'short' : 'full');

// Smart Answer Input - renders the control resolveAnswerControl names for
// this question's (answer_type, field_key) pair.
export const SmartAnswerInput = ({ value, onChange, testId, suggestions = [], answerType, fieldKey, procurementType, statedUnit, statedValue, currencyCode, locationProposal, onLocationPrefill }) => {
  const control = resolveAnswerControl({ answerType, fieldKey, suggestions });

  if (control === 'location') {
    return (
      <LocationDropdown
        value={value}
        onChange={onChange}
        proposal={locationProposal}
        onPrefill={onLocationPrefill}
      />
    );
  }

  if (control === 'date') {
    return (
      <DatePickerInput
        value={value}
        onChange={onChange}
        placeholder={DATE_PLACEHOLDERS[fieldKey] || 'Select date'}
      />
    );
  }

  // Item 5 (answers-step batch, 2026-07-22): budget was previously free
  // text for a number - a real numeric input so the value is validated at
  // entry, not just at submit. A pre-existing legacy TEXT budget answer
  // (an in-flight procurement saved before this change) is parsed via
  // answersValidation.parseLegacyBudget by the caller before it ever
  // reaches `value` here - this component only renders whatever it's given.
  if (control === 'budget') {
    return (
      <div className="flex items-center gap-2">
        {/* B2-B: displays the already-answered rfp_currency for context -
            never a second currency selector here (one fact, one source;
            the dedicated currency question, rendered earlier in the same
            list, is the only place it's ever asked). Renders nothing if
            currency isn't answered yet - never a placeholder. */}
        {currencyCode && (
          <span
            className="shrink-0 px-2.5 py-2 rounded-md border border-zinc-200 bg-zinc-50 text-sm font-medium text-zinc-600"
            data-testid={testId ? `${testId}-currency` : undefined}
          >
            {currencyCode}
          </span>
        )}
        <NumberInput
          min="0"
          step="any"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter estimated budget..."
          className="bg-zinc-50 border-zinc-200 focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF]"
          data-testid={testId}
        />
      </div>
    );
  }

  // PROC-000054 item 1 (revised): quantity is a composite number+unit
  // value, by field_key identity - not a bare number, and not trusting the
  // generated answer_type. See QuantityCompositeInput above for the full
  // rationale (it self-fetches uom_master; StepAnswers.jsx separately seeds
  // `value` from parsed_requirement.quantity when empty, and normalizes a
  // pre-existing legacy/prose stored answer into canonical form on load).
  if (control === 'quantity') {
    return (
      <QuantityCompositeInput
        value={value}
        onChange={onChange}
        testId={testId}
        procurementType={procurementType}
        statedUnit={statedUnit}
        statedValue={statedValue}
      />
    );
  }

  // 'textarea' carries chips; 'number', 'text', 'currency' (never
  // actually routed here) and 'unrecognised' all render the plain input,
  // exactly as they did before the resolver existed.
  return (
    <div>
      {control === 'textarea' ? (
        /* GROWS WITH ITS CONTENT (item 8, 2026-09-03). This had a fixed
           min-h-[100px] and no growth wiring, so anything past the box
           scrolled inside it (~6 lines at this font). Now: the shared base
           rows, the shared helper on mount and on change, and the shared
           ceiling class - identical to the intake textarea. */
        <Textarea
          ref={autoResizeTextarea}
          rows={TEXTAREA_BASE_ROWS}
          value={value || ''}
          onChange={(e) => { autoResizeTextarea(e.target); onChange(e.target.value); }}
          placeholder="Enter detailed specifications..."
          className={`${GROWING_TEXTAREA_CLASS} bg-zinc-50 border-zinc-200 focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF]`}
          data-testid={testId}
        />
      ) : (
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Your answer..."
          className="bg-zinc-50 border-zinc-200 focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF]"
          data-testid={testId}
        />
      )}
      <AnswerSuggestionChips suggestions={suggestions} onSelect={onChange} />
    </div>
  );
};

export default SmartAnswerInput;
